import React, { useEffect } from 'react';
import classNames from 'classnames';

import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';

import { AccordionItem, Heading, Link, Spinner, Button } from '@mm/components';
import { AngleLeftLightIcon, ArrowStatefulIcon } from '@mm/icons';

import { Doc, useMyGoals } from './use-my-goals';
import { EmailInput } from '../../../../../common/modals/InviteUserModal/EmailInput/EmailInput';

import './SlideoutMyGoals.pcss';

export type SlideoutMyGoalsProps = {
  className?: string;
  visible?: boolean;
  onClose?: () => void;
  onReady?: () => void;
};

const navs = [
  { id: 'guides', title: 'Guides' },
  { id: 'qa', title: 'FAQ' },
  { id: 'feedback', title: 'Feedback' },
];

const formatString = (s = '', { length }: { length?: number } = {}) => {
  const esc = s.replace?.(/&#8232;/g, ' ');

  if (!length) {
    return esc;
  }

  return esc.slice(0, length) + (s.length > length ? '...' : '');
};

export const SlideoutMyGoals = (props: SlideoutMyGoalsProps) => {
  const { className, visible, onClose, onReady } = props;

  const [state, dispatch, { init, submitFeedback }] = useMyGoals();

  const doc: Doc = state.guides?.items?.find(({ id }) => id === state.activeId);
  const resourceView = state.menu === 'guides' && doc;

  const canSubmit =
    state.feedback.text &&
    state.feedback.email?.isValid &&
    !state.feedback.busy;

  const handleClose = () => {
    dispatch({ type: 'submitEnd', payload: { err: true } });
    onClose?.();
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (
      state.ready &&
      [state.qa?.items, state.guides?.items].some(({ length }) => !!length)
    ) {
      onReady?.();
    }
  }, [state.ready]);

  return (
    <div
      className={classNames(className, 'slideout-my-goals', {
        'slideout-my-goals--visible': visible,
      })}
    >
      <Menu className="slideout-my-goals__menu">
        <Menu.Header
          className="slideout-my-goals__header"
          onClose={handleClose}
        >
          {!resourceView && <Heading size={3}>My goals</Heading>}

          {resourceView && (
            <div
              className="slideout-my-goals__header-nav"
              onClick={() => dispatch({ type: 'navigate', payload: 'guides' })}
            >
              <AngleLeftLightIcon />
              <Heading size={3}>See all goals</Heading>
            </div>
          )}
        </Menu.Header>
        <Menu.Content className="slideout-my-goals__content">
          {!resourceView && (
            <div className="slideout-my-goals__nav">
              {navs.map(({ id, title }) => (
                <span
                  key={id}
                  onClick={() => dispatch({ type: 'navigate', payload: id })}
                  className={classNames('slideout-my-goals__nav-item', {
                    'slideout-my-goals__nav-item--active': state.menu === id,
                  })}
                >
                  {title}
                </span>
              ))}
            </div>
          )}

          {state.menu === 'guides' && !state.activeId && (
            <div className="slideout-my-goals__guides">
              {state.guides?.items?.map(guide => (
                <div
                  key={guide.id}
                  className="slideout-my-goals__guide"
                  onClick={() => dispatch({ type: 'open', payload: guide.id })}
                >
                  <Heading className="slideout-my-goals__title" size={2}>
                    {formatString(guide.title)}

                    <ArrowStatefulIcon
                      size={16}
                      className="slideout-my-goals__arrow"
                      direction="right"
                    />
                  </Heading>
                  <Heading className="slideout-my-goals__description" size={4}>
                    {formatString(guide.description, { length: 80 })}
                  </Heading>
                </div>
              ))}
            </div>
          )}

          {resourceView && (
            <div className="slideout-my-goals__guide">
              <Heading className="slideout-my-goals__title" size={2}>
                {formatString(doc.title)}
              </Heading>
              <Heading className="slideout-my-goals__description" size={4}>
                {formatString(doc.description)}
              </Heading>

              {doc.attachmentUrl && (
                <div className="slideout-my-goals__attachment">
                  <iframe
                    className="slideout-my-goals__iframe"
                    width="420"
                    height="315"
                    src={doc.attachmentUrl}
                    title={doc.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {doc.url && (
                <Link href={doc.url} isBlank>
                  Visit help section
                </Link>
              )}
            </div>
          )}

          {state.menu === 'qa' && (
            <div className="slideout-my-goals__qas">
              {state.qa?.items?.map(guide => (
                <AccordionItem
                  key={guide.id}
                  title={guide.title}
                  closeOnOutsideClick
                >
                  <span className="slideout-my-goals__qa-description">
                    {guide.description}
                  </span>
                </AccordionItem>
              ))}
            </div>
          )}

          {state.menu === 'feedback' && !state.feedback.success && (
            <div className="slideout-my-goals__feedback">
              <textarea
                placeholder="let us know about any issues you face or ideas improve your workflow"
                value={state.feedback.idea}
                onChange={e =>
                  dispatch({
                    type: 'feedback',
                    payload: e.target.value,
                  })
                }
              />
              <EmailInput
                className="slideout-my-goals__input"
                onChange={payload => {
                  dispatch({
                    type: 'email',
                    payload,
                  });
                }}
              />
              <Button disabled={!canSubmit} onClick={submitFeedback}>
                {state.feedback.busy && <Spinner show standalone />}
                {!state.feedback.busy && 'Send'}
              </Button>
            </div>
          )}

          {state.menu === 'feedback' && state.feedback.success && (
            <Heading size={1}>Thanks for your feedback</Heading>
          )}

          {state.busy && <Spinner show standalone={true} />}
        </Menu.Content>
      </Menu>
    </div>
  );
};
